import React, { FC, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useCurrency, useI18n, useLogger } from '~Hooks';
import { Box, Divider } from '~Styles';
import { GreenButton } from '~Styles';
import DenominationButton, { StyledButton } from './DenominationButton';
import RemoveDenominationButton from './RemoveDenominationButton';
import {
  EURO_DENOMINATIONS,
  SEK_DENOMINATIONS,
  NOK_DENOMINATIONS,
  DKK_DENOMINATIONS,
  TRY_DENOMINATIONS,
  GBP_DENOMINATIONS,
} from './consts';
import NumberInput from '~ComponentsImproved/Numpad';

const MainContainer = styled(Box)`
  height: 100%;
`;

const ContentWrapper = styled(Box)`
  flex: 1;
  min-height: 0;
`;

const DenominationsGrid = styled(Box)`
  max-width: 400px;
`;

const RightPanel = styled(Box)`
  min-width: 300px;
  flex: 1;
`;

const ScrollableList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  max-height: 226px;
  min-height: 0;
`;

const FixedBottomSection = styled(Box)`
  position: relative;

  &:before {
    content: '';
    background: linear-gradient(to top, var(--colorBlueGray1200) 0%, transparent 100%);
    height: 60px;
    width: 100%;
    position: absolute;
    top: -60px;
    left: 0;
    pointer-events: none;
  }
`;

interface Props {
  /** Final submit representing an integer (in cents) */
  onSubmit: (amount: number) => void;
}

/** Helper component to count cash - especially useful in start- & end of day */
const CashCounter: FC<Props> = ({ onSubmit }) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const currency = useCurrency();
  const logger = useLogger('cash-counter');
  const isHandy = window.innerWidth <= 600;

  const [enteredDenominations, setEnteredDenominations] = useState<Record<number, number>>({});
  const [showManualEntry, setShowManualEntry] = useState<boolean>(false);
  const [manualAmount, setManualAmount] = useState<number>(0);

  // Determine which denominations to show based on currency
  const currencyDenominations: number[] = useMemo(() => {
    switch (storage.currency.code) {
      case 'EUR':
        return EURO_DENOMINATIONS;
      case 'SEK':
        return SEK_DENOMINATIONS;
      case 'NOK':
        return NOK_DENOMINATIONS;
      case 'DKK':
        return DKK_DENOMINATIONS;
      case 'TRY':
        return TRY_DENOMINATIONS;
      case 'GBP':
        return GBP_DENOMINATIONS;
      default:
        logger.warn('Unsupported currency for cash counter', {
          currencyCode: storage.currency.code,
        });
        return SEK_DENOMINATIONS; // Fallback to SEK
    }
  }, [storage.currency.code, logger]);

  // Calculate total amount from entered denominations (in cents)
  const totalAmountInCents = useMemo(() => {
    const denominationsTotal = Object.entries(enteredDenominations).reduce(
      (sum, [denomination, quantity]) => {
        return sum + Number(denomination) * quantity;
      },
      0,
    );
    // Add manual amount to total
    return denominationsTotal + manualAmount;
  }, [enteredDenominations, manualAmount]);

  // Handle adding or updating a denomination entry
  const handleDenominationSubmit = (denomination: number, quantity: number) => {
    setEnteredDenominations((prev) => ({
      ...prev,
      [denomination]: quantity,
    }));
    logger.info('Denomination entered', { denomination, quantity });
  };

  // Handle removing a denomination entry
  const handleRemoveDenomination = (denomination: number) => {
    setEnteredDenominations((prev) => {
      const updated = { ...prev };
      delete updated[denomination];
      return updated;
    });
    logger.info('Denomination removed', { denomination });
  };

  // Handle final submit
  const submitButton = useButton({
    onClick: () => {
      logger.info('Cash counter submitted', { totalAmountInCents });
      onSubmit(totalAmountInCents);
    },
    disabled: totalAmountInCents < 0,
  });

  // Get sorted list of entered denominations (highest to lowest)
  const enteredDenominationsList = useMemo(() => {
    return Object.entries(enteredDenominations)
      .map(([denomination, quantity]) => ({
        denominationInCents: Number(denomination),
        quantity,
        subtotalInCents: Number(denomination) * quantity,
      }))
      .sort((a, b) => b.denominationInCents - a.denominationInCents);
  }, [enteredDenominations]);

  // Handle manual entry
  const handleManualEntry = (amount: number) => {
    const amountInCents = Math.round(amount * 100);
    setManualAmount(amountInCents);
    setShowManualEntry(false);
    logger.info('Manual amount entered', { amountInCents });
  };

  const enterManuallyButton = useButton({
    onClick: () => setShowManualEntry(true),
  });

  return (
    <>
      <MainContainer display="flex" flexDirection="column" gap="medium">
        <Heading level={isHandy ? 3 : 2} color="var(--colorWhite)">
          {i18n('CashCounter.Title')}
        </Heading>

        <ContentWrapper display="flex" gap="large">
          <Box display="flex" flexDirection="column" gap="medium">
            <DenominationsGrid display="flex" flexWrap="wrap" gap="small">
              {currencyDenominations.map((denomination) => (
                <DenominationButton
                  key={denomination}
                  denomination={denomination}
                  onSubmit={handleDenominationSubmit}
                  quantity={enteredDenominations[denomination] || 0}
                />
              ))}
              <StyledButton {...enterManuallyButton} fluid>
                {i18n('CashCounter.Manually')}
              </StyledButton>
            </DenominationsGrid>
          </Box>

          <RightPanel display="flex" flexDirection="column" gap="small">
            {/* Scrollable list section */}
            <ScrollableList display="flex" flexDirection="column" gap="xs">
              {enteredDenominationsList.length === 0 && manualAmount === 0 ? (
                <Box display="flex" justifyContent="center">
                  <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)">
                    {i18n('CashCounter.NothingEntered')}
                  </Paragraph>
                </Box>
              ) : (
                <>
                  {enteredDenominationsList.map(
                    ({ denominationInCents, quantity, subtotalInCents }) => (
                      <Box
                        key={denominationInCents}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap="medium">
                        <Paragraph level={isHandy ? 4 : 3} color="var(--colorWhite)">
                          {currency.format(denominationInCents)} x {quantity} ={' '}
                          {currency.format(subtotalInCents)}
                        </Paragraph>
                        <RemoveDenominationButton
                          onRemove={() => handleRemoveDenomination(denominationInCents)}
                        />
                      </Box>
                    ),
                  )}

                  {manualAmount > 0 && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap="medium">
                      <Paragraph level={isHandy ? 4 : 3} color="var(--colorWhite)">
                        {i18n('CashCounter.ManualEntry')}: {currency.format(manualAmount)}
                      </Paragraph>
                      <RemoveDenominationButton onRemove={() => setManualAmount(0)} />
                    </Box>
                  )}
                </>
              )}
            </ScrollableList>

            {/* Fixed bottom section */}
            <FixedBottomSection display="flex" flexDirection="column" gap="small">
              <Divider />

              <Heading level={isHandy ? 4 : 3} color="var(--colorWhite)">
                {i18n('App.Total')}: {currency.format(totalAmountInCents)}
              </Heading>

              <GreenButton {...submitButton}>{i18n('General.Submit')}</GreenButton>
            </FixedBottomSection>
          </RightPanel>
        </ContentWrapper>
      </MainContainer>

      {showManualEntry && (
        <NumberInput
          decimal
          title={i18n('CashCounter.EnterTotal')}
          onSubmit={(amount) => handleManualEntry(Number(amount))}
          onClose={() => setShowManualEntry(false)}
          small={isHandy}
        />
      )}
    </>
  );
};

export default CashCounter;
